import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, ShieldCheck } from 'lucide-react';
import api from './api';
import { useToast } from '../hooks/use-toast';

let stripePromise = null;
const getStripe = (pk) => { if (!stripePromise && pk) stripePromise = loadStripe(pk); return stripePromise; };

const WARNING = 'All payments must be made through the EA Lead Solutions platform. Paying an operative directly voids your buyer protection, your right to dispute, and may result in permanent account suspension. Direct payments breach our Terms & Conditions and a Direct Engagement Fee of 20% of the job value will apply.';

const InnerForm = ({ paymentIntentId, amount, label, onSuccess, requireAuth }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [authed, setAuthed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (requireAuth && !authed) { setError('Please tick the authorisation box to continue.'); return; }
    setBusy(true); setError('');
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/dashboard/client` },
      redirect: 'if_required',
    });
    if (result.error) { setError(result.error.message || 'Payment failed'); setBusy(false); return; }
    try {
      const r = await api.post('/payments/confirm', { paymentIntentId });
      toast({ title: 'Payment successful', description: `${label} paid.` });
      onSuccess(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Payment captured but confirmation failed. Please refresh.');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="stripe-payment-form">
      <div className="rounded-lg bg-[#e9f3ec] p-3 text-center"><p className="text-sm text-gray-600">{label}</p><p className="text-2xl font-bold text-[#1b4f72]">£{Number(amount).toFixed(2)}</p></div>
      <PaymentElement />
      {requireAuth && (
        <label className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-[12px] leading-snug text-amber-900" data-testid="auto-charge-consent">
          <input type="checkbox" data-testid="auto-charge-consent-checkbox" checked={authed} onChange={(e) => setAuthed(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#3a9b5c]" />
          <span>I authorise EA Lead Solutions to charge this payment method for the remaining 85% balance automatically if I do not confirm payment or raise a valid dispute within 48 hours of the operative marking the job complete. Authorisation is given now, at booking, and no further approval is required at the time of that charge.</span>
        </label>
      )}
      {error && <p className="text-sm text-red-600" data-testid="payment-error">{error}</p>}
      <button disabled={!stripe || busy || (requireAuth && !authed)} data-testid="pay-now-btn" className="w-full rounded-full bg-[#3a9b5c] px-6 py-3 font-semibold text-white hover:bg-[#318150] disabled:opacity-60">{busy ? 'Processing…' : `Pay £${Number(amount).toFixed(2)}`}</button>
      <p className="flex items-start gap-2 text-[11px] leading-snug text-gray-400"><ShieldCheck size={26} className="shrink-0 text-[#3a9b5c]" /><span>{WARNING}</span></p>
    </form>
  );
};

// props: { clientSecret, publishableKey, paymentIntentId, amount, label, onClose, onSuccess }
const PaymentModal = ({ clientSecret, publishableKey, paymentIntentId, amount, label, onClose, onSuccess, requireAuth }) => {
  const stripeObj = getStripe(publishableKey);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="payment-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-lg font-bold text-[#1b4f72]">Secure Payment</h3><button onClick={onClose} data-testid="close-payment-modal" className="text-gray-400 hover:text-gray-700"><X size={20} /></button></div>
        {clientSecret && stripeObj ? (
          <Elements stripe={stripeObj} options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#3a9b5c' } } }}>
            <InnerForm paymentIntentId={paymentIntentId} amount={amount} label={label} onSuccess={onSuccess} requireAuth={requireAuth} />
          </Elements>
        ) : <p className="py-10 text-center text-sm text-gray-500">Loading secure payment…</p>}
      </div>
    </div>
  );
};

export const PLATFORM_WARNING = WARNING;
export default PaymentModal;
